import { useEffect, useState } from 'react';

import CheckboxField from '../../components/CheckboxField/CheckboxField';
import Description from '../../components/Description/Description';
import Group from '../../components/Group/Group';
import MonetaryField from '../../components/MonetaryField/MonetaryField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import RadioField from '../../components/RadioField/RadioField';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Subtitle from '../../components/Subtitle/Subtitle';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useFieldToggle from '../../hooks/useFieldToggle';
import { ValidationResultType } from '../../hooks/useFieldValidation';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

const applicantCreditRemarksOptions = [
    {
        id: 'true',
        label: 'Ja',
    },
    {
        id: 'false',
        label: 'Nei',
    },
];

const ExpensePage = () => {
    const translate = useTranslation();
    const { totalSections, totalAboutYouPages } = usePagesConfig();
    const { currentPage } = usePagesContext();
    useComponentTracker('ExpensePage');
    const defaultPagesAction = useDefaultPagesAction();
    const dispatch = useAppDispatch();

    useSetBannerType('bannerOne');

    const applicantMortgageToggle = useAppSelector(selectFieldValue)('applicantMortgageToggle') === 'true';
    const applicantCarLoanToggle = useAppSelector(selectFieldValue)('applicantCarLoanToggle') === 'true';
    const applicantCarLeasingMonthlyCostToggle =
        useAppSelector(selectFieldValue)('applicantCarLeasingMonthlyCostToggle') === 'true';
    const applicantStudentLoanToggle = useAppSelector(selectFieldValue)('applicantStudentLoanToggle') === 'true';

    useFieldToggle(applicantMortgageToggle, ['applicantMortgageDebt', 'applicantMortgageMonthlyCost']);
    useFieldToggle(applicantCarLoanToggle, ['applicantCarLoanSum', 'applicantCarLoanMonthlyCost']);
    useFieldToggle(applicantCarLeasingMonthlyCostToggle, ['applicantCarLeasingMonthlyCost']);
    useFieldToggle(applicantStudentLoanToggle, ['applicantStudentLoanSum', 'applicantStudentLoanMonthlyCost']);

    const [requiredFields, setRequiredFields] = useState([]);
    const validationErrors = useValidation(requiredFields);

    useEffect(() => {
        const result: any = [];

        if (applicantMortgageToggle) {
            result.push('applicantMortgageDebt');
            result.push('applicantMortgageMonthlyCost');
        }

        if (applicantCarLoanToggle) {
            result.push('applicantCarLoanSum');
            result.push('applicantCarLoanMonthlyCost');
        }

        if (applicantCarLeasingMonthlyCostToggle) {
            result.push('applicantCarLeasingMonthlyCost');
        }

        if (applicantStudentLoanToggle) {
            result.push('applicantStudentLoanSum');
            result.push('applicantStudentLoanMonthlyCost');
        }

        result.push('applicantCreditRemarks');

        setRequiredFields(result);
    }, [
        applicantMortgageToggle,
        applicantCarLoanToggle,
        applicantCarLeasingMonthlyCostToggle,
        applicantStudentLoanToggle,
    ]);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next') {
            if (validationErrors.length > 0) {
                scrollToFieldWithError(validationErrors);
                validationErrors.forEach((validationError) => {
                    dispatch(addOrUpdateFormField(validationError));
                });
                return false;
            }
        }

        return defaultPagesAction(action);
    };

    const minValueValidation = async (value?: string): Promise<ValidationResultType> => {
        const isValid = (value ?? 0) > '0';

        return {
            valid: isValid,
            errorMessageKey: isValid ? undefined : translate('field.applicant.min-value.error'),
        };
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.expense.title')} />
            <Description text={translate('page.expense.description')} />
            <ShowDetailsModal
                label={translate('dialog.know-more.label')}
                dialogTitle={translate('dialog.know-more.label')}
                dialogContent={translate('dialog.expense.content')}
            />

            <CheckboxField name={'applicantMortgageToggle'} label={translate('field.applicant-mortage-toggle.label')} />
            {applicantMortgageToggle && (
                <Group>
                    <MonetaryField
                        name="applicantMortgageDebt"
                        label={translate('field.applicant-debt.label')}
                        customValidation={minValueValidation}
                    />
                    <MonetaryField
                        name="applicantMortgageMonthlyCost"
                        label={translate('field.applicant-debt-monthly-cost.label')}
                        customValidation={minValueValidation}
                    />
                </Group>
            )}

            <CheckboxField name={'applicantCarLoanToggle'} label={translate('field.applicant-car-loan-toggle.label')} />
            {applicantCarLoanToggle && (
                <Group>
                    <MonetaryField
                        name="applicantCarLoanSum"
                        label={translate('field.applicant-debt.label')}
                        customValidation={minValueValidation}
                    />
                    <MonetaryField
                        name="applicantCarLoanMonthlyCost"
                        label={translate('field.applicant-debt-monthly-cost.label')}
                        customValidation={minValueValidation}
                    />
                </Group>
            )}

            <CheckboxField
                name={'applicantCarLeasingMonthlyCostToggle'}
                label={translate('field.applicant-car-leasing-monthly-cost-toggle.label')}
            />
            {applicantCarLeasingMonthlyCostToggle && (
                <MonetaryField
                    name="applicantCarLeasingMonthlyCost"
                    label={translate('field.applicant-car-leasing-monthly-cost.label')}
                    customValidation={minValueValidation}
                />
            )}

            <CheckboxField
                name={'applicantStudentLoanToggle'}
                label={translate('field.applicant-student-loan-toggle.label')}
            />
            {applicantStudentLoanToggle && (
                <Group>
                    <MonetaryField
                        name="applicantStudentLoanSum"
                        label={translate('field.applicant-debt.label')}
                        customValidation={minValueValidation}
                    />
                    <MonetaryField
                        name="applicantStudentLoanMonthlyCost"
                        label={translate('field.applicant-debt-monthly-cost.label')}
                        customValidation={minValueValidation}
                    />
                </Group>
            )}
            <Subtitle text={translate('field.applicant-credit-remarks.label')} />
            <RadioField name="applicantCreditRemarks" options={applicantCreditRemarksOptions} />
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default ExpensePage;
